import { Component, DoCheck, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { DATE_FORMAT } from 'src/app/shared/constants';
import {
    DxButtonModule,
    DxDateBoxModule,
    DxNumberBoxModule,
    DxRadioGroupModule,
    DxSelectBoxModule,
    DxValidationGroupModule,
    DxValidatorModule,
} from 'devextreme-angular';
import {
    DayOfWeek,
    RecurrenceFrequency,
    RecurrenceMode,
    RecurrenceRuleDto,
} from '@services';
import EnumUtils from 'src/app/shared/enum-utils';
import { DayOfWeekPipe } from 'src/app/pipes/day-of-week.pipe';

interface RecurrenceModeOption {
    value: RecurrenceMode;
    text: string;
}

@Component({
    selector: 'app-invoice-purchase-order-details-recurrence',
    imports: [
        CommonModule,
        DxButtonModule,
        DxDateBoxModule,
        DxNumberBoxModule,
        DxRadioGroupModule,
        DxSelectBoxModule,
        DxValidationGroupModule,
        DxValidatorModule,
    ],
    providers: [DayOfWeekPipe],
    templateUrl: './invoice-purchase-order-details-recurrence.html',
})
export class InvoicePurchaseOrderDetailsRecurrenceComponent implements OnInit, DoCheck {
    @Input() recurrenceRule?: RecurrenceRuleDto;
    @Input() issueDate?: Date;
    @Input() isReadOnly: boolean = false;

    @Output() recurrenceRuleChange = new EventEmitter<RecurrenceRuleDto | undefined>();

    private dayOfWeekPipe = inject(DayOfWeekPipe);
    private previousIssueDateTime?: number;

    public dateFormat = DATE_FORMAT;
    public RecurrenceFrequency = RecurrenceFrequency;
    public recurrenceText: string = '';
    public isRecurrenceRuleVisible = false;
    public monthlyModeOptions: RecurrenceModeOption[] = [];
    public yearlyModeOptions: RecurrenceModeOption[] = [];

    frequencyOptions = EnumUtils.getEnumOptions(RecurrenceFrequency);
    daysOfWeekOptions = EnumUtils.getEnumOptions(DayOfWeek, (v) => this.dayOfWeekPipe.transform(v));

    ngOnInit() {
        if (this.recurrenceRule == undefined) return;

        this.previousIssueDateTime = this.issueDate?.getTime();

        this.loadRecurrenceRule();
    }

    ngDoCheck(): void {
        const issueDateTime = this.issueDate?.getTime();

        if (issueDateTime !== this.previousIssueDateTime) {
            this.previousIssueDateTime = issueDateTime;

            if (this.recurrenceRule !== undefined) {
                this.loadRecurrenceRule();
            }
        }
    }

    showRecurrenceRule() {
        this.isRecurrenceRuleVisible = !this.isRecurrenceRuleVisible;
        this.loadRecurrenceRule();
    }

    loadRecurrenceRule() {
        const issueDate = this.issueDate ?? new Date();
        this.recurrenceText = this.formatRecurrencePreview(this.recurrenceRule, this.issueDate);

        this.recurrenceRule = this.recurrenceRule?.frequency !== undefined
                ? RecurrenceRuleDto.fromJS(this.recurrenceRule.toJSON())
                : this.buildDefaultRecurrenceRule(issueDate);
        this.recurrenceRuleChange.emit(this.recurrenceRule);

        this.monthlyModeOptions = [
            { value: RecurrenceMode.OnDate, text: this.getMonthlyOnDateLabel(issueDate), },
            { value: RecurrenceMode.NthWeekday, text: this.getMonthlyNthWeekdayLabel(issueDate), },
        ];
        this.yearlyModeOptions = [
            { value: RecurrenceMode.OnDate, text: this.getYearlyOnDateLabel(issueDate), },
            { value: RecurrenceMode.NthWeekday, text: this.getYearlyNthWeekdayLabel(issueDate), },
        ];
    }

    deleteRecurrenceRule() {
        this.isRecurrenceRuleVisible = false;
        this.recurrenceRule = undefined;
        this.recurrenceRuleChange.emit(this.recurrenceRule);
        this.recurrenceText = '';
    }

    isWeekdaySelected(day: DayOfWeek): boolean {
        return (
            this.recurrenceRule!.weekdays?.includes(day) ??
            false
        );
    }

    toggleWeekday(day: DayOfWeek) {
        if (this.isReadOnly) return;

        const weekdays =
            this.recurrenceRule!.weekdays ??
            (this.recurrenceRule!.weekdays = []);
        const index = weekdays.indexOf(day);

        if (index === -1) {
            weekdays.push(day);
        } else {
            weekdays.splice(index, 1);
        }
    }

    addYears(date: Date, years: number): Date {
        const result = new Date(date);
        result.setFullYear(result.getFullYear() + years);
        return result;
    }

    getOrdinalSuffix(n: number): string {
        const mod100 = n % 100;
        if (mod100 >= 11 && mod100 <= 13) return `${n}th`;

        switch (n % 10) {
            case 1:
                return `${n}st`;
            case 2:
                return `${n}nd`;
            case 3:
                return `${n}rd`;
            default:
                return `${n}th`;
        }
    }

    getWeekdayOccurrenceInMonth(date: Date): number {
        return Math.ceil(date.getDate() / 7);
    }

    getWeekdayName(day: DayOfWeek): string {
        return DayOfWeek[day];
    }

    getMonthName(date: Date): string {
        return formatDate(date, 'MMMM', 'en-US');
    }

    buildDefaultRecurrenceRule(issueDate: Date): RecurrenceRuleDto {
        return RecurrenceRuleDto.fromJS({
            interval: 1,
            frequency: RecurrenceFrequency.Monthly,
            mode: RecurrenceMode.OnDate,
            weekdays: [],
            until: this.addYears(issueDate, 1),
        });
    }

    getMonthlyOnDateLabel(issueDate: Date): string {
        return `On day ${issueDate.getDate()}`;
    }

    getMonthlyNthWeekdayLabel(issueDate: Date): string {
        const ordinal = this.getOrdinalSuffix(
            this.getWeekdayOccurrenceInMonth(issueDate),
        );
        const weekday = this.getWeekdayName(issueDate.getDay() as DayOfWeek);
        return `On the ${ordinal} ${weekday}`;
    }

    getYearlyOnDateLabel(issueDate: Date): string {
        return `On ${this.getMonthName(issueDate)} ${issueDate.getDate()}`;
    }

    getYearlyNthWeekdayLabel(issueDate: Date): string {
        const ordinal = this.getOrdinalSuffix(
            this.getWeekdayOccurrenceInMonth(issueDate),
        );
        const weekday = this.getWeekdayName(issueDate.getDay() as DayOfWeek);
        return `On the ${ordinal} ${weekday} of ${this.getMonthName(issueDate)}`;
    }

    pluralizeUnit(unit: string, interval: number): string {
        return interval === 1 ? unit : `${unit}s`;
    }

    formatWeekdayList(weekdays: DayOfWeek[] | undefined): string {
        if (!weekdays?.length) return '';

        const names = [...weekdays]
            .sort((a, b) => a - b)
            .map(this.getWeekdayName);

        return names.length === 1
            ? names[0]
            : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    }

    formatRecurrencePreview(rule: RecurrenceRuleDto | undefined, issueDate: Date | undefined): string {
        if (!rule || !issueDate) return '';

        const interval = rule.interval ?? 1;
        const intervalPrefix = interval === 1 ? '' : `${interval} `;

        let onClause = '';
        let unit = 'month';

        switch (rule.frequency) {
            case RecurrenceFrequency.Weekly:
                unit = 'week';
                onClause = ` on ${this.formatWeekdayList(rule.weekdays)}`;
                break;
            case RecurrenceFrequency.Yearly:
                unit = 'year';
                onClause =
                    rule.mode === RecurrenceMode.NthWeekday
                        ? ` ${this.getYearlyNthWeekdayLabel(issueDate).toLowerCase()}`
                        : ` ${this.getYearlyOnDateLabel(issueDate).toLowerCase()}`;
                break;
            case RecurrenceFrequency.Monthly:
            default:
                onClause =
                    rule.mode === RecurrenceMode.NthWeekday
                        ? ` ${this.getMonthlyNthWeekdayLabel(issueDate).toLowerCase()}`
                        : ` ${this.getMonthlyOnDateLabel(issueDate).toLowerCase()}`;
                break;
        }

        const until = rule.until
            ? formatDate(rule.until, DATE_FORMAT, 'en-US')
            : '';

        return `Repeats every ${intervalPrefix}${this.pluralizeUnit(unit, interval)}${onClause}, until ${until}.`;
    }
}
